import { environment } from 'src/environments/environment';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, map, switchMap } from 'rxjs/operators';
import { defer, EMPTY, Observable, of } from 'rxjs';
import { canChangeOrderStatus, canCheckoutOrder, isOrderPaid, isOrderUnpaid, needsOrderCheckout, normalizeOrderResponse, orderAmount, OrderActionError, OrderPaymentRequiredError } from './order-state';
import { PaymentServices } from './payment.service';
import { LOCAL_REQUEST_FEEDBACK } from './reporting-api.service';

export enum AscDesc{ 
  LATEST = 1,
  OLDEST = 0
}

export interface IOrder{ 
  customerName: string;
  customerPhoneNumber: string;
  carBrand: string;
  carModel: string;
  carPlateNumber: string;
  branchId?: number;
  notes?: string
}

export interface IQuery { 
    pageSize?: number;
    pageNumber?: number;
    statusId?: number;
    From?: string;
    To?: string;
    BranchId? : number,
    ResultOrder?: AscDesc;
    orderId?: string;
    branchId?: number,
    SearchByText?: string,
    SearchBy?:number,
    SpotId?: number | null ,
    note?:string|null
}

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private readonly pendingActions = new Set<string>();
  private readonly actionOptions = { context: new HttpContext().set(LOCAL_REQUEST_FEEDBACK, true) };

  constructor(
    private httpClient: HttpClient,
    private payments: PaymentServices,
  ) { }


  listOrders({pageNumber, pageSize, statusId, From, To, BranchId, ResultOrder,SearchByText,SearchBy,SpotId}: IQuery) {
    const baseUrl = `${environment.apiUrl}Order`;
    const params = {} as IQuery;
    if (pageNumber) params['pageNumber'] = pageNumber;
    if (pageSize) params['pageSize'] = pageSize;
    if (statusId) params['statusId'] = statusId;
    if (BranchId) params['BranchId'] = BranchId;
    if (SearchBy) params['SearchBy'] = SearchBy;
    if (SearchByText) params['SearchByText'] = SearchByText;
    if (SpotId) params['SpotId'] = SpotId;

    if (From) params['From'] =  new Date(From).toISOString();
    if (To) params['To'] = new Date(To).toISOString();
    if(ResultOrder !== undefined) params['ResultOrder'] = ResultOrder;

    const queryParams = new URLSearchParams(params as Record<string, string>).toString();    
    return this.httpClient.get(`${baseUrl}?${queryParams}`).pipe(map(normalizeOrderResponse));
  }
  
  orderStatus(){
    return this.httpClient.get(`${environment.apiUrl}Lookup/order-statuses`)
  }

  getBranchList(){
    return this.httpClient.get(`${environment.apiUrl}User/GetUserBranches`)
  }

  getCarBrandsList(){
    return this.httpClient.get(`${environment.apiUrl}Lookup/car-brands`)
  }

  orderDetails({ orderId }: { orderId: string }){
    return this.httpClient.get(`${environment.apiUrl}Order/Details/${orderId}`).pipe(map(normalizeOrderResponse));
  }

  changeStatus({orderId, statusId, branchId}: IQuery){
    const data = {orderId, statusId: Number(statusId), branchId};
    return this.withOrderAction(orderId!, () => this.currentOrderForAction(data).pipe(switchMap(order => {
      if (data.statusId === 5 && !isOrderPaid(order) && orderAmount(order) > 0) {
        throw new OrderPaymentRequiredError(order);
      }
      return this.httpClient.put(`${environment.apiUrl}Order/status`, data, this.actionOptions);
    })));
  }

  UpdateDeliveredAndNotPaidOrderStatusDto({orderId, statusId, branchId,note}: IQuery){
    const data = {orderId, statusId: 5, branchId, note: note?.trim()};
    return this.withOrderAction(orderId!, () => this.currentOrderForAction(data).pipe(switchMap(order => {
      if (!data.note || !isOrderUnpaid(order)) throw new OrderActionError('orders.actionUnavailable');
      return this.httpClient.put(`${environment.apiUrl}Order/UpdateDeliveredAndNotPaidOrderStatusDto`, data, this.actionOptions);
    })));
  }

  // Capture the ticket identity and await payment before attempting delivery.
  // Re-read after payment so another employee's delivery cannot trigger a retry.
  payAndDeliver(orderId: string, branchId: number, isCash: boolean): Observable<any> {
    const data = { orderId, branchId, statusId: 5 };
    return this.withOrderAction(orderId, () => this.currentOrderForAction(data).pipe(
      switchMap(order => isOrderPaid(order) || orderAmount(order) === 0 ? of(null) : this.payments.payOffline(orderId, isCash).pipe(map(response => {
        if ((response as any)?.Ok === false) throw new OrderActionError('payment_failed');
        return response;
      }))),
      switchMap(() => this.currentOrderForAction(data)),
      switchMap(() => this.httpClient.put(`${environment.apiUrl}Order/status`, data, this.actionOptions))
    ));
  }

  private currentOrder({orderId, branchId}: IQuery): Observable<any> {
    return this.orderDetails({ orderId: orderId! }).pipe(map((response: any) => {
      const order = response?.Value?.Data ?? response?.Data;
      if (!order || String(order.Id).toLowerCase() !== String(orderId).toLowerCase()
        || (branchId != null && Number(order.BranchId) !== Number(branchId))) {
        throw new OrderActionError('orders.actionUnavailable');
      }
      return order;
    }));
  }

  private currentOrderForAction(query: IQuery): Observable<any> {
    return this.currentOrder(query).pipe(map(order => {
      const statusId = Number(query.statusId);
      if (!canChangeOrderStatus(order.Status, statusId)) throw new OrderActionError('orders.actionUnavailable');
      if (statusId === 5 && needsOrderCheckout(order)) throw new OrderActionError('mustMakeCheckout');
      if (statusId === 5 && !isOrderPaid(order)
        && (!Number.isFinite(orderAmount(order)) || (orderAmount(order) > 0 && !isOrderUnpaid(order)))) {
        throw new OrderActionError('orders.actionUnavailable');
      }
      return order;
    }));
  }

  private withOrderAction(orderId: string, work: () => Observable<any>): Observable<any> {
    return defer(() => {
      if (this.pendingActions.has(orderId)) return EMPTY;
      this.pendingActions.add(orderId);
      return defer(work).pipe(finalize(() => this.pendingActions.delete(orderId)));
    });
  }

  getBranchPercentage(branchId:number){
    return this.httpClient.get(`${environment.apiUrl}Branch/GetBranchPercentage?id=${branchId}`)
  }

  createOrder(orderData:any){
    return this.httpClient.post(`${environment.apiUrl}Order/create`,orderData)
  }
  updateOrder(data:any, id:number) {
    let url = `${environment.apiUrl}Order/${id}`;
    return this.httpClient.put(url, data);
  }

  getOrderStatus(id:number | string){
    return this.httpClient.get(`${environment.apiUrl}Order/GetOrderStatuses?id=${id}`).pipe(map(normalizeOrderResponse));
  }
  getOrderConfigurations(){
    return this.httpClient.get(`${environment.apiUrl}Order/configurations`);
  }
  
  GetPrintableOrder(orderId: string, branchId: string){
    return this.httpClient.get(`${environment.apiUrl}Order/print-customer?id=${orderId}&branchId=${branchId}`);
  }
  GetBranchParkingFloorsAndFloorSpots( branchId: number,isEditMode:boolean,spotId:any){
    return this.httpClient.get(`${environment.apiUrl}Order/GetBranchParkingFloorsAndFloorSpots?branchId=${branchId}&isEdit=${isEditMode}&spotId=${spotId}`);
  }
  GetOccupiedBranchParkingSpots( branchId: number){
    return this.httpClient.get(`${environment.apiUrl}Order/GetOccupiedBranchParkingSpots?branchId=${branchId}`);
  }
  UpdateCustomerMobileNumber( orderId: string,customerMobileNumber:string){
    return this.httpClient.get(`${environment.apiUrl}Order/UpdateCustomerMobileNumber?orderId=${orderId}&customerMobileNumber=${customerMobileNumber}`);
  }
  DailyBranchReport( branchId: number,dateFrom:Date,dateTo:Date){
    return this.httpClient.get(`${environment.apiUrl}Order/DailyBranchReport?branchId=${branchId}&dateFrom=${dateFrom}&dateTo=${dateTo}`);
  }
  PrintDailyBranchReport( token :string, branchId: number,dateFrom:Date,dateTo:Date){
    return this.httpClient.get(`${environment.apiUrl}Order/PrintDailyBranchReport?token=${token}&branchId=${branchId}&dateFrom=${dateFrom}&dateTo=${dateTo}`);
  }
  CalculateTiersPrice( orderId: string){
    return this.withOrderAction(orderId, () => this.currentOrder({orderId}).pipe(switchMap(order => {
      if (!canCheckoutOrder(order)) throw new OrderActionError('orders.actionUnavailable');
      return this.httpClient.get(`${environment.apiUrl}Order/CalculateTiersPrice?orderId=${orderId}`, this.actionOptions);
    })));
  }

  getOrderReviewsBulk(orderIds: string[]) {
    return this.httpClient.post(`${environment.apiUrl}Order/reviews/bulk`, orderIds);
  }
}
